import { debounce } from 'lodash-es';
import { mapState } from 'vuex';

/**
 * @module components/SlideToggle
 * @vue-data {Observer} parentObserver=null - 부보 dom 변화 옵저버
 * @vue-data {Observer} observer=null - dom 변화 옵저버
 * @vue-data {Number} height=0 - 현재 변화하는 높이
 * @vue-data {Number} oriHeight=0 - 최초 기본 높이
 * @vue-data {Function} resizeHandler=null - 리사이즈 때 발상해는 함수
 * @vue-data {Number} speed=null - 속도
 * @vue-prop {String} tag=true - 컴포넌트 tag
 * @vue-prop {String} selector=li - css class
 * @vue-prop {String} styleName=.clientHeight - 외부 data 바인딩될 활성화 값
 * @vue-prop {Number} duration=0.3 - 활성화 css class명
 * @vue-computed {Boolean} open - 열림 유무
 * @vue-computed {VueComponent | null} parentComponent - 부모 vue컴포넌트
 * @vue-computed {Element} activateElement - parentObserver 대상 element
 */
export default {
  name : 'slide-toggle',
  props: {
    tag: {
      type   : String,
      require: true
    },
    selector: {
      type   : String,
      default: 'li'
    },
    styleName: {
      type   : String,
      default: '.clientHeight'
    },
    duration: {
      type   : Number,
      default: 0.3
    }
  },
  data() {
    return {
      parentObserver: null,
      observer      : null,
      height        : 0,
      oriHeight     : 0,
      resizeHandler : null,
      speed         : null
    };
  },
  watch: {
    open() {
      this.resize();
    }
  },
  computed: {
    ...mapState('view', {
      open: (state) => state.header.open
    }),
    parentComponent() {
      return this.$parent.$options.name === this.$options.name
        ? this.$parent
        : null;
    },
    activateElement() {
      return this.$el.parentElement;
    }
  },
  mounted() {
    this.setHeight();

    this.$nextTick(() => {
      this.oriHeight = this.height;
    });

    // 부모에 slideToggle 존재 시 mutation 설정
    if (this.parentComponent) {
      this.initParentObserve();
    }

    // 자식 element 변화에 대한 observe
    this.initObserve();

    // resize 시 height 재조정
    this.resizeHandler = debounce(this.resize, 400);
    window.addEventListener('resize', this.resizeHandler);
  },
  methods: {
    /**
     * 옵저버 초기화 함수
     */
    initObserve() {
      this.observe = new MutationObserver((mutations) => {
        mutations.forEach((/* mutation */) => {
          // updated hook 대신
          this.updateSelf();
        });
      });

      this.observe.observe(this.$el, {
        childList: true
      });
    },
    /**
     * 부모 옵저버 초기화 함수
     */
    initParentObserve() {
      this.parentObserver = new MutationObserver((mutations) => {
        mutations.forEach((/* mutation */) => {
          // updated hook 대신
          this.updateParent();
        });
      });

      this.parentObserver.observe(this.activateElement, {
        attributes: true
      });
    },
    /**
     * 옵저버 mutations 에 실행하는 함수
     */
    updateSelf() {
      this.setHeight();

      this.$nextTick(() => {
        const mutationHeight = this.height - this.oriHeight; // 추가 & 제거된 높이 저장
        this.oriHeight = this.height; // 변경된 높이 오리지널에 반영

        if (this.parentComponent) {
          this.parentComponent.setHeight({
            exceptionHeight: mutationHeight // 부모에 전달
          });
        }
      });
    },
    /**
     * 부모 옵저버 mutations 에 실행하는 함수
     */
    updateParent() {
      this.setHeight();

      if (this.parentComponent) {
        this.parentComponent.setHeight({
          exceptionHeight: this.activateElement.classList.contains('active')
            ? this.oriHeight // 부모 높이 변경
            : this.oriHeight * -1 // 부모 높이 복구
        });
      }
    },
    /**
     * 높이 설정하는 함수
     */
    setHeight({ exceptionHeight = 0 } = {}) {
      const targets = this.$el.querySelectorAll(this.selector);

      if (exceptionHeight) {
        this.height += exceptionHeight;
      } else {
        this.height = [...targets.entries()]
          .map((item) => {
            return this.getStyle(item[1]);
          })
          .reduce((a, b) => a + b, 0);
      }
    },
    /**
     * 높이 기준이되는 스타일 반환
     * @param {Element} el 높이 반환할 대상
     * @returns 높이 반환
     */
    getStyle(el) {
      return (
        parseInt(
          this.styleName.charAt(0) === '.'
            ? el[this.styleName.substr(1)]
            : window.getComputedStyle(el)[this.styleName]
        ) || 0
      );
    },
    /**
     * 리사이즈
     */
    resize() {
      this.speed = 0;

      this.$nextTick(() => {
        this.updateSelf();

        setTimeout(() => {
          this.updateParent();

          this.speed = null;
        }, 10);
      });
    }
  },
  beforeDestroy() {
    if (this.parentObserver) {
      this.parentObserver.disconnect();
    }

    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  },
  render(h) {
    return h(
      this.tag,
      {
        style: [
          {
            '--h': `${this.height}px`,
            '--s': `${this.speed !== null ? this.speed : this.duration}s`
          }
        ]
      },
      this.$slots.default
    );
  }
};
